<template>
    <div class="min-h-screen bg-background font-sans selection:bg-primary/10">
        <main class="pt-32 pb-20 container mx-auto px-4">
            <div class="grid lg:grid-cols-12 gap-12 items-start">

                <div class="lg:col-span-7 space-y-8">
                    <div class="flex items-center gap-4 text-xs font-medium tracking-widest text-zinc-500 uppercase">
                        <span class="bg-zinc-100 px-2 py-1 text-zinc-900 rounded">Kids' Art Classes</span>
                        <span>Learn &bull; Practice &bull; Shine</span>
                    </div>

                    <h1 class="text-5xl lg:text-7xl leading-tight tracking-tight text-zinc-900">
                        A joyful space for young artists to grow.
                    </h1>

                    <p class="text-lg lg:text-xl text-zinc-500 leading-relaxed max-w-xl">
                        From sketching basics to watercolor confidence—classes are designed to build skills, creativity,
                        and pride in every child's work.
                    </p>

                    <div class="flex flex-wrap gap-4 pt-4">
                        <a href="#" @click.prevent="dmEnquire()"
                            class="bg-zinc-900 text-white px-6 py-3 rounded flex items-center gap-3 hover:bg-zinc-800 transition"
                            role="button" aria-label="DM to Enquire via WhatsApp">
                            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path
                                    d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                            </svg>
                            DM to Enquire
                            <span>&rarr;</span>
                        </a>
                        <a href="https://www.youtube.com/@artpulse_by_shruti" target="_blank"
                            class="border border-zinc-900 px-6 py-3 rounded flex items-center gap-3 hover:bg-zinc-50 transition">
                            Watch on YouTube
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14">
                                </path>
                            </svg>
                        </a>
                    </div>

                    <div class="grid sm:grid-cols-3 gap-4 pt-8">
                        <div class="border border-zinc-100 p-6 rounded-sm bg-white shadow-sm">
                            <div class="mb-4 text-zinc-400">
                                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z">
                                    </path>
                                </svg>
                            </div>
                            <h4 class="font-semibold mb-2">Skill-first</h4>
                            <p class="text-sm text-zinc-500 leading-relaxed">Foundations that actually stick—line, form,
                                shading, color.</p>
                        </div>
                        <div class="border border-zinc-100 p-6 rounded-sm bg-white shadow-sm">
                            <div class="mb-4 text-zinc-400">
                                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z">
                                    </path>
                                </svg>
                            </div>
                            <h4 class="font-semibold mb-2">Confidence</h4>
                            <p class="text-sm text-zinc-500 leading-relaxed">Gentle feedback + encouragement to help
                                kids keep going.</p>
                        </div>
                        <div class="border border-zinc-100 p-6 rounded-sm bg-white shadow-sm">
                            <div class="mb-4 text-zinc-400">
                                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z">
                                    </path>
                                </svg>
                            </div>
                            <h4 class="font-semibold mb-2">Community</h4>
                            <p class="text-sm text-zinc-500 leading-relaxed">A supportive environment where creativity
                                feels safe.</p>
                        </div>
                    </div>
                </div>

                <div class="lg:col-span-5 bg-zinc-50 border border-zinc-100 p-8 lg:p-12 rounded-sm space-y-8"
                    style="box-shadow: 1px 0px 2px 2px #e3e3e3;">
                    <div>
                        <h2 class="serif text-3xl mb-4 flex items-center gap-3 italic">
                            <span class="not-italic text-2xl">✨</span> Enquire via DM
                        </h2>
                        <p class="text-zinc-600">Class details (age groups, timings, fees, and availability) are shared
                            on Instagram DM for now.</p>
                    </div>

                    <div class="space-y-6">
                        <div>
                            <h5 class="text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-4">What Kids
                                Learn</h5>
                            <ul class="space-y-2 text-zinc-800">
                                <li class="flex items-start gap-2"><span>▪</span> Sketching basics</li>
                                <li class="flex items-start gap-2"><span>▪</span> Shading & proportions</li>
                                <li class="flex items-start gap-2"><span>▪</span> Watercolor techniques</li>
                                <li class="flex items-start gap-2"><span>▪</span> Creative projects</li>
                            </ul>
                        </div>

                        <div>
                            <h5 class="text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-4">Formats</h5>
                            <ul class="space-y-2 text-zinc-800">
                                <li class="flex items-start gap-2"><span>▪</span> Online</li>
                                <li class="flex items-start gap-2"><span>▪</span> In-person (as available)</li>
                                <li class="flex items-start gap-2"><span>▪</span> 1:1 or small groups</li>
                            </ul>
                        </div>
                    </div>

                    <a href="https://www.instagram.com/artpulse_by_shruti/" target="_blank"
                        class="block bg-zinc-900 text-white text-center py-4 rounded font-medium hover:bg-zinc-800 transition">
                        DM @artpulse_by_shruti &rarr;
                    </a>

                    <div class="flex items-center gap-3 text-xs text-zinc-400 border-t border-zinc-200 pt-6">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z">
                            </path>
                        </svg>
                        Photos below are from recent class moments.
                    </div>
                </div>

            </div>
            <div class="mt-16 space-y-12">

                <section>
                    <div class="flex justify-between items-baseline mb-6">
                        <div>
                            <h2 class="text-3xl font-serif text-gray-900 leading-tight">Class highlights</h2>
                            <p class="text-gray-500 text-sm mt-1 font-sans">A peek into practice sessions, student work,
                                and happy milestones.</p>
                        </div>
                        <span class="text-[10px] tracking-widest text-gray-400 font-sans uppercase font-bold">Tap any
                            photo to view</span>
                    </div>

                    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div v-for="item in galleryItems" :key="item.id"
                            class="aspect-[4/3] bg-gray-200 overflow-hidden shadow-sm">
                            <img :src="item.small_fullurl" class="w-full h-full object-cover">
                        </div>
                    </div>
                    <div v-if="!galleryItems.length" class="text-sm text-gray-400 italic">
                        Gallery coming soon…
                    </div>
                </section>

                <section
                    class="bg-gray-50 border border-gray-100 p-8 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div class="space-y-1 text-center md:text-left">
                        <div
                            class="flex items-center justify-center md:justify-start gap-2 text-[10px] tracking-widest text-gray-400 font-sans uppercase font-bold">
                            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd"
                                    d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 110-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z"
                                    clip-rule="evenodd" />
                            </svg>
                            Ready to start?
                        </div>
                        <h3 class="text-2xl font-serif text-gray-900 leading-tight">
                            Send a quick DM with your child's age + preferred medium.
                        </h3>
                        <p class="text-gray-400 text-sm font-sans italic">
                            I'll share the latest schedule and the best class option.
                        </p>
                    </div>

                    <a href="https://www.instagram.com/artpulse_by_shruti/" target="_blank"
                        class="bg-black text-white px-8 py-3 font-sans text-xs tracking-widest uppercase font-bold flex items-center gap-3 hover:bg-gray-800 transition-all whitespace-nowrap">
                        DM on Instagram
                        <span class="text-lg">→</span>
                    </a>
                </section>

            </div>
        </main>
    </div>
</template>
<script>
import api from '@/services/api';
import ConstantPage from '@/components/utilities/ConstantPage.vue';
export default {
    name: 'KidsClassesPage',
    mixins: [ConstantPage],
    data() {
        return {
            galleryItems: [],
            contactForm: {
                name: '',
                email: '',
                message: '',
            },

            isSending: false,
            showBookingDialog: false,
            selectedWorkshop: null,
            bookingForm: {
                name: '',
                email: '',
                phone: '',
            },
            isBookingSubmitting: false,
            toast: {
                visible: false,
                title: '',
                description: '',
            },
        };
    },
    mounted() {
        this.fetchGalleryItems();
    },
    methods: {
        async fetchGalleryItems() {
            try {
                let param = {
                    companyid: this.COMPANY_ID,
                    category_name: 'kids-classess'
                }
                const response = await api.post('api/gallery/list', param);
                if (response.data?.data.length == 0) {
                    return;
                }
                this.galleryItems = response.data?.data[0].gallery_media || [];
            } catch (error) {
                console.error('Failed to fetch gallery items:', error);
            }
        }
        ,
        dmEnquire() {
            const phone = '14806360819'
            const text = 'Hello'
            const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
            window.open(url, '_blank')
        }
    }
}

</script>